/**
 * Backend-neutral client for the Workday RaaS HRIS config surface (integrations
 * + data sources). Components reach these operations through
 * useHrisConfigClient() (see hooks/useHrisConfigClient) rather than calling the
 * generated services directly, so a later v2 phase can route a tenant to the
 * data-management backend by registering a second implementation here — with no
 * component change.
 *
 * The config entity types are re-exported under facade-owned names, aliased to
 * the marketprice (api) generated models today. When the api HRIS surface is
 * retired, retarget these aliases (and supply the data-management client) here
 * in one place; the interface signature and every consumer stay put.
 *
 * Enums (HRISConfigName, HRISIntegrationType, ...) are intentionally NOT
 * re-exported: TS enums are nominal, so they cannot be transparently swapped by
 * a type alias the way structural model types can. Call sites keep importing
 * enum values from 'types'; the retirement phase reconciles them alongside the
 * component switch to data-management.
 */
import { HrisIntegrationService, HrisDataSourceService } from 'types';
import type {
  CreateHrisIntegrationRequest as ApiCreateHrisIntegrationRequest,
  HRISAuth as ApiHRISAuth,
  HRISConfigName as ApiHRISConfigName,
  HRISDataSource as ApiHRISDataSource,
  HRISIntegration as ApiHRISIntegration,
  SaveHRISDataSourceRequest as ApiSaveHRISDataSourceRequest,
  UpdateHrisIntegrationRequest as ApiUpdateHrisIntegrationRequest,
} from 'types';

export type HRISIntegration = ApiHRISIntegration;
export type HRISAuth = ApiHRISAuth;
export type HRISDataSource = ApiHRISDataSource;
export type CreateHrisIntegrationRequest = ApiCreateHrisIntegrationRequest;
export type UpdateHrisIntegrationRequest = ApiUpdateHrisIntegrationRequest;
export type SaveHRISDataSourceRequest = ApiSaveHRISDataSourceRequest;

// Inline response shapes the generated services return but don't name as models.
export type TestHrisUrlResult = { result?: string; status?: string; message?: string };
export type SyncHrisIntegrationResult = {
  result?: string;
  dataTypes?: Array<'jobs' | 'employees' | 'ranges'>;
};
export type DeleteHrisIntegrationResult = { message?: string };

/**
 * The HRIS config operations the settings UI performs, independent of which
 * backend serves them. Method shapes mirror the generated services so each
 * implementation is a thin delegate and call sites change only their import.
 */
export interface HrisConfigClient {
  listIntegrations(params: { type?: string; includeAuth?: boolean }): Promise<HRISIntegration[]>;
  createIntegration(params: { requestBody: CreateHrisIntegrationRequest }): Promise<HRISIntegration>;
  updateIntegration(params: { id: number; requestBody: UpdateHrisIntegrationRequest }): Promise<HRISIntegration>;
  deleteIntegration(params: { id: number }): Promise<DeleteHrisIntegrationResult>;
  testUrl(params: {
    requestBody: { auth?: ApiHRISAuth; url?: string; configName?: ApiHRISConfigName };
  }): Promise<TestHrisUrlResult>;
  syncIntegration(params: { id: number }): Promise<SyncHrisIntegrationResult>;
  listDataSources(params: { dataType?: string }): Promise<HRISDataSource[]>;
  saveDataSources(params: { requestBody: SaveHRISDataSourceRequest }): Promise<HRISDataSource[]>;
}

/** Marketprice (api/) implementation: delegates 1:1 to the generated services. */
export const marketpriceHrisConfigClient: HrisConfigClient = {
  listIntegrations: (params) => HrisIntegrationService.listHrisIntegrations(params),
  createIntegration: (params) => HrisIntegrationService.createHrisIntegration(params),
  updateIntegration: (params) => HrisIntegrationService.updateHrisIntegration(params),
  deleteIntegration: (params) => HrisIntegrationService.deleteHrisIntegration(params),
  testUrl: (params) => HrisIntegrationService.testHrisUrl(params),
  syncIntegration: (params) => HrisIntegrationService.syncHrisIntegration(params),
  listDataSources: (params) => HrisDataSourceService.listHrisDataSources(params),
  saveDataSources: (params) => HrisDataSourceService.saveHrisDataSources(params),
};

/**
 * Registration point for the data-management implementation. Its API is a later
 * v2 phase, so it is absent today: useHrisConfigClient falls back to marketprice
 * until this is assigned, keeping the routing flag a safe no-op in the interim.
 */
export const dataManagementHrisConfigClient: HrisConfigClient | null = null;
